import type { AclEntityMinInfoEntityTypeEnum } from "@eu.mdotm.api/sphere";
import type { RichAccessControl } from "@eu.mdotm.api/smart-workspaces";
import { type EntityMetadata, spawnShareDialogSmart, updateAclWithFeedbacks } from "../spawn-share-dialog";
import { type PlatformErrorAreas, reportPlatformError } from "$root/api/error-reporting";
import { referenceRepository } from "$root/functional-areas/portfolio/references-repository";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { universeRepository } from "$root/functional-areas/universe/repository";
import type { SpawnResult } from "@mdotm/shadcn/legacy/react-extensions";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { switchExpr } from "@mdotm/shadcn/legacy/utils";
import { useCallback } from "react";
import { useUserValue } from "../../user";
import Disclaimer from "../Disclaimer";
import type { Areas } from "../checkers/all";
import { marketViewRepository } from "$root/functional-areas/market-view/repository";
import { universeWithGroupsRepository } from "$root/functional-areas/universe/universeWithGroupsRepository";

type CustomAclEntityMinInfoEntityTypeEnum = AclEntityMinInfoEntityTypeEnum | "REFERENCE";
function aclController(entity: CustomAclEntityMinInfoEntityTypeEnum) {
	const controller = switchExpr(entity, {
		INVESTMENT: () => ({ set: portfolioRepository.updateACL, get: portfolioRepository.getACL.fetchQuery }),
		UNIVERSE: () => ({ set: universeRepository.updateACL, get: universeRepository.getACL.fetchQuery }),
		GROUP_UNIVERSE: () => ({
			set: universeWithGroupsRepository.updateACL,
			get: universeWithGroupsRepository.getACL.fetchQuery,
		}),
		MARKET_VIEW: () => ({ set: marketViewRepository.updateACL, get: marketViewRepository.getACL.fetchQuery }),
		BENCHMARK: () => ({
			set: referenceRepository.updateBenchmarkACL,
			get: referenceRepository.getBenchmarkACL.fetchQuery,
		}),
		REFERENCE: () => ({
			set: referenceRepository.updateReferenceACL,
			get: referenceRepository.getReferenceACL.fetchQuery,
		}),
	});

	return controller;
}

const areasByEntity = {
	BENCHMARK: "benchmark",
	INVESTMENT: "portfolio",
	MARKET_VIEW: "marketView",
	UNIVERSE: "universe",
	GROUP_UNIVERSE: "universe",
	REFERENCE: "portfolio",
} satisfies { [key in CustomAclEntityMinInfoEntityTypeEnum]: Areas };

const platformErrorByEntity = {
	BENCHMARK: "benchmark",
	INVESTMENT: "portfolio",
	MARKET_VIEW: "market-views",
	UNIVERSE: "universe",
	GROUP_UNIVERSE: "universe",
	REFERENCE: "target-portfolio",
} satisfies { [key in CustomAclEntityMinInfoEntityTypeEnum]: PlatformErrorAreas };

const remapEntityTypeForPreview = {
	BENCHMARK: "BENCHMARK",
	GROUP_UNIVERSE: "GROUP_UNIVERSE",
	INVESTMENT: "INVESTMENT",
	MARKET_VIEW: "MARKET_VIEW",
	REFERENCE: "REFERENCE",
	UNIVERSE: "UNIVERSE",
} satisfies Record<CustomAclEntityMinInfoEntityTypeEnum, AclEntityMinInfoEntityTypeEnum>;

const remapEntityDisclaimer = {
	BENCHMARK: "BENCHMARK",
	GROUP_UNIVERSE: "GROUP_UNIVERSE",
	INVESTMENT: "INVESTMENT",
	MARKET_VIEW: "MARKET_VIEW",
	REFERENCE: "INVESTMENT",
	UNIVERSE: "UNIVERSE",
} satisfies Record<CustomAclEntityMinInfoEntityTypeEnum, AclEntityMinInfoEntityTypeEnum>;

/**
 * Given an entity class, spawn the dialog to update the permission
 *
 * @param {("INVESTMENT" | "UNIVERSE" | "BENCHMARK" | "MARKET_VIEW")} entity "INVESTMENT" | "UNIVERSE" | "BENCHMARK" | "MARKET_VIEW"
 * @returns {Object} { aclSpawn }
 */
export function useUpdateAccessListControlDialog(entity: CustomAclEntityMinInfoEntityTypeEnum): {
	aclSpawn: (name?: string, uuid?: string) => Promise<SpawnResult<void>>;
} {
	const user = useUserValue();
	const entityController = aclController(entity);
	const entityControllerRef = useUnsafeUpdatedRef(entityController);

	const updateAccessPermission = useCallback(
		async (acl: RichAccessControl[], uuid?: string) => {
			if (!uuid) {
				throw new Error(`missing ${entity} uuid`);
			}
			await entityControllerRef.current.set(uuid, acl);
		},
		[entityControllerRef, entity],
	);

	const aclSpawn = useCallback(
		async (name?: string, uuid?: string) => {
			try {
				const entityMetadata: EntityMetadata = {
					uuid: uuid!,
					name: name ?? "-",
					entityType: remapEntityTypeForPreview[entity],
				};
				return await spawnShareDialogSmart({
					userId: user.id,
					async saveAclHandler({ aclByEntity }) {
						await updateAclWithFeedbacks(() => updateAccessPermission(aclByEntity[uuid!], uuid));
					},
					header: `Share "${name ?? "-"}"`,
					disclaimerProvider: (entities) => <Disclaimer entity={remapEntityDisclaimer[entity]} entities={entities} />,
					customerAndLinkedAclProvider: () => entityControllerRef.current.get(uuid!),
					entityMetadata,
					area: areasByEntity[entity],
				});
			} catch (err) {
				reportPlatformError(err, "WARN", platformErrorByEntity[entity], {
					message: `share entity "${name ?? "-"}" with uuid "${uuid}"`,
				});
				throw err;
			}
		},
		[entity, entityControllerRef, updateAccessPermission, user.id],
	);

	return { aclSpawn };
}
